import React, { useEffect, useMemo, useState } from 'react';
import { Input, TextArea, Toggle, FormRow } from './FormElements';
import { UrgencySelector } from './UrgencySelector';
import { PlatformSelector } from './PlatformSelector';
import { customFetch } from '../../../utils/Auth';
import { MAX_MESSAGE_LENGTH } from '../utils/constants';
import { environment } from '../../../config/environment';

export const NotificationForm = ({
  formData,
  updateField,
  togglePlatform,
  setUrgency,
  toggleSchedule,
  characterCount,
  scheduleError,
  labelClassName,
}) => {
  const [audienceMode, setAudienceMode] = useState(() => {
    const ta = formData.targetAudience;
    if (!ta || ta === 'all_users') return 'all';
    if (ta.includes(',')) return 'user';
    return 'org';
  });
  const [orgOptions, setOrgOptions] = useState([]);
  const [allDoctors, setAllDoctors] = useState([]);
  const [doctorSearch, setDoctorSearch] = useState('');
  const [filteredDoctors, setFilteredDoctors] = useState([]);

  useEffect(() => {
    const loadAudienceSources = async () => {
      try {
        const orgRes = await customFetch(
          `${environment.apiUrl}/doctors/orgs-for-update`,
          { method: 'GET' }
        );
        const orgData = await orgRes.json();
        setOrgOptions(orgData.map((org) => org.organization).filter(Boolean));
      } catch (err) {
        setOrgOptions([]);
      }

      try {
        const docRes = await customFetch(
          `${environment.apiUrl}/doctors/all-doctors`,
          { method: 'GET' }
        );
        const docData = await docRes.json();
        setAllDoctors(Array.isArray(docData) ? docData : []);
      } catch (err) {
        setAllDoctors([]);
      }
    };

    loadAudienceSources();
  }, []);

  useEffect(() => {
    if (doctorSearch.trim()) {
      const search = doctorSearch.trim().toLowerCase();
      setFilteredDoctors(
        allDoctors.filter((doc) => {
          const name = doc.user_name?.toLowerCase() || '';
          const id = doc.user_id?.toLowerCase() || '';
          const email = doc.user_email?.toLowerCase() || '';
          return name.includes(search) || id.includes(search) || email.includes(search);
        })
      );
    } else {
      setFilteredDoctors([]);
    }
  }, [doctorSearch, allDoctors]);

  useEffect(() => {
    if (audienceMode === 'all') {
      updateField('targetAudience', 'all_users');
    }
  }, [audienceMode, updateField]);

  // Sync audienceMode when orgOptions loads (for edit mode)
  useEffect(() => {
    const ta = formData.targetAudience;
    if (!ta || ta === 'all_users') return;
    if (orgOptions.length > 0 && orgOptions.includes(ta)) {
      setAudienceMode('org');
    } else if (ta.includes(',') || (ta && !orgOptions.includes(ta))) {
      setAudienceMode('user');
    }
  }, [formData.targetAudience, orgOptions]);

  // Derive selectedDoctors from formData.targetAudience (single source of truth)
  const selectedDoctors = useMemo(() => {
    if (audienceMode !== 'user' || !formData.targetAudience) return [];
    const ids = formData.targetAudience.split(',').map((s) => s.trim()).filter(Boolean);
    return ids.map((id) => allDoctors.find((d) => d.user_id === id)).filter(Boolean);
  }, [audienceMode, formData.targetAudience, allDoctors]);

  return (
    <>
    <Input
      label="Title"
      required
      placeholder="Enter notification title"
      value={formData.title}
      onChange={(e) => updateField('title', e.target.value)}
      labelClassName={labelClassName}
    />

    <TextArea
      label="Message"
      required
      placeholder="Enter your notification message..."
      value={formData.notificationMessage}
      onChange={(e) => updateField('notificationMessage', e.target.value)}
      characterCount={characterCount}
      maxLength={MAX_MESSAGE_LENGTH}
      labelClassName={labelClassName}
    />

    <UrgencySelector value={formData.urgency} onChange={setUrgency} labelClassName={labelClassName} />

    <PlatformSelector value={formData.platforms} onChange={togglePlatform} labelClassName={labelClassName} />

    <div className="mt-2 space-y-3">
      <label className="block text-sm font-semibold text-slate-700">Target Recipient(s)</label>
      <div className="flex gap-2">
        {['all', 'org', 'user'].map((mode) => (
          <button
            key={mode}
            type="button"
            className={`px-3 py-1.5 rounded border text-sm ${
              audienceMode === mode
                ? 'bg-purple-100 border-purple-500'
                : 'border-slate-200'
            }`}
            onClick={() => {
              setAudienceMode(mode);
              setDoctorSearch('');
              if (mode === 'all') {
                updateField('targetAudience', 'all_users');
              } else if (mode === 'org') {
                updateField('targetAudience', '');
              } else if (mode === 'user') {
                updateField('targetAudience', '');
              }
            }}
          >
            {mode === 'all' ? 'All Users' : mode === 'org' ? 'Organization' : 'Specific Users'}
          </button>
        ))}
      </div>

      {audienceMode === 'all' && (
        <div className="text-sm text-slate-500">All users will receive this notification.</div>
      )}

      {audienceMode === 'org' && (
        <select
          className="w-full border rounded-lg px-3 py-2 text-sm"
          value={formData.targetAudience || ''}
          onChange={(e) => updateField('targetAudience', e.target.value)}
        >
          <option value="">Select organization</option>
          {orgOptions.map((org) => (
            <option key={org} value={org}>
              {org}
            </option>
          ))}
        </select>
      )}

      {audienceMode === 'user' && (
        <div className="space-y-2">
          <input
            type="text"
            value={doctorSearch}
            onChange={(e) => setDoctorSearch(e.target.value)}
            placeholder="Search by name, email, or user ID..."
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
          {selectedDoctors.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedDoctors.map((doc) => (
                <button
                  key={doc.user_id}
                  type="button"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 text-sm"
                  onClick={() => {
                    const ids = selectedDoctors
                      .filter((d) => d.user_id !== doc.user_id)
                      .map((d) => d.user_id);
                    updateField('targetAudience', ids.join(','));
                  }}
                >
                  {doc.user_name}
                  <span className="text-purple-600">×</span>
                </button>
              ))}
            </div>
          )}
          {doctorSearch && filteredDoctors.length > 0 && (
            <div className="border rounded-md max-h-56 overflow-y-auto bg-white">
              {filteredDoctors
                .filter((doc) => !selectedDoctors.some((d) => d.user_id === doc.user_id))
                .map((doc) => (
                  <div
                    key={doc.user_id}
                    className="px-3 py-2 hover:bg-purple-50 cursor-pointer"
                    onClick={() => {
                      const ids = [...selectedDoctors.map((d) => d.user_id), doc.user_id];
                      updateField('targetAudience', ids.join(','));
                      setDoctorSearch('');
                    }}
                  >
                    <div className="text-sm font-medium">
                      {doc.user_name}{' '}
                      {doc.user_email ? (
                        <span className="text-xs text-slate-500">({doc.user_email})</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-slate-500">{doc.user_id}</div>
                  </div>
                ))}
            </div>
          )}
          {doctorSearch && filteredDoctors.filter((doc) => !selectedDoctors.some((d) => d.user_id === doc.user_id)).length === 0 && (
            <div className="text-sm text-slate-400">
              {filteredDoctors.length > 0 ? 'All matching doctors already selected.' : 'No doctors found.'}
            </div>
          )}
        </div>
      )}
    </div>

    <div className="mt-6">
      <Input
        label="Duration (minutes)"
        type="number"
        placeholder="e.g., 60 — leave blank to never expire"
        value={formData.duration}
        onChange={(e) => {
          const nextValue = e.target.value;
          updateField('duration', nextValue === '' ? '' : parseInt(nextValue, 10));
        }}
        labelClassName={labelClassName}
      />
    </div>

    <div className="mt-5">
      <Toggle
        label="Schedule for later"
        checked={formData.isScheduled}
        onChange={toggleSchedule}
        labelClassName={labelClassName}
      />

      {formData.isScheduled && (
        <div className="mt-3">
          <FormRow>
            <Input
              label="Send Date"
              type="date"
              value={formData.scheduledDate}
              onChange={(e) => updateField('scheduledDate', e.target.value)}
              labelClassName={labelClassName}
            />
            <Input
              label="Send Time"
              type="time"
              value={formData.scheduledTime}
              onChange={(e) => updateField('scheduledTime', e.target.value)}
              labelClassName={labelClassName}
            />
          </FormRow>
          {scheduleError && (
            <p role="alert" className="-mt-3 text-sm text-red-600">
              {scheduleError}
            </p>
          )}
        </div>
      )}
    </div>
    </>
  );
};
